import { Alert, Heading } from '@mm/components';
import { ArrowStatefulIcon, CloseCircleIcon, PictureIcon } from '@mm/icons';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useUploadActions } from '@mm/state/actions/uploadActions';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import page from '@mm/state/reducers/page';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import TextInput from '../../../../common/components/TextInput';
import { ProfileImage } from '../../../../editor/features/liveChat/components/ProfileImage/ProfileImage';
import styles from './FacebookPageCustomizationPane.module.css';
import { ReactComponent as Upload } from './upload-icon.svg';

const UPLOAD_SIZE_LIMIT = 8 * 1024 * 1024;

type Props = {
  facebookPage: EnhancedBillingPage;
  onClose: (options?: {
    updatedProfilePicture?: boolean;
    keepPageSwitcherOpen?: boolean;
  }) => void;
};
export const FacebookPageCustomizationPane = (props: Props) => {
  const { facebookPage, onClose } = props;

  const {
    fetchCustomSquarePicture,
    updateCustomSquarePicture,
    removeCustomSquarePicture,
  } = useConnectedPageCRUDActions();
  const connectedPageCRUDActions = useConnectedPageCRUDActions();
  const { uploadImage } = useUploadActions();

  const [busy, setBusy] = useState(false);
  const [queuedAction, setQueuedAction] = useState<
    'remove' | 'update' | undefined
  >();
  const [newImageFile, setNewImageFile] = useState<File>();
  const [rejectedFile, setRejectedFile] = useState<File>();
  const [newImageUrl, setNewImageUrl] = useState<string>();
  // undefined - loading
  // null - no custom image
  // string - custom image
  const [customSquarePicture, setCustomSquarePicture] = useState<
    string | null
  >();
  const [pageName, setPageName] = useState(facebookPage.name);

  useEffect(() => {
    const loadCustomSquarePicture = async () => {
      setCustomSquarePicture(
        (await fetchCustomSquarePicture(facebookPage.id))?.squarePicture,
      );
    };
    loadCustomSquarePicture();
  }, []);

  const onSave = async () => {
    setBusy(true);
    switch (queuedAction) {
      case 'remove':
        await removeCustomSquarePicture(facebookPage.id);
        onClose({ updatedProfilePicture: true });
        break;
      case 'update':
        if (newImageFile) {
          const extension = newImageFile.type.split('/').pop();
          if (extension) {
            const imageId = await uploadImage({
              content: newImageFile,
              extension,
            });
            const result = await updateCustomSquarePicture(
              facebookPage.id,
              imageId,
            );
            onClose({ updatedProfilePicture: true });
          }
        }
        if (pageName !== facebookPage?.name) {
          await connectedPageCRUDActions.update({
            id: facebookPage.id,
            name: pageName,
          });
          onClose({ keepPageSwitcherOpen: true });
        }
        break;
      default:
        onClose();
        break;
    }
  };
  const acceptImage = (file: File) => {
    setQueuedAction('update');
    setNewImageFile(file);
    try {
      const reader = new FileReader();
      reader.onload = e => setNewImageUrl(e.target?.result as string);
      reader.readAsDataURL(file);
    } catch {}
  };

  const onPageNameChange = e => {
    setQueuedAction('update');
    setPageName(e.target.value);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'],
    maxSize: UPLOAD_SIZE_LIMIT,
    onDrop: (files: File[], rejectedFiles: File[]) => {
      if (files[0]) {
        acceptImage(files[0]);
        setRejectedFile(undefined);
      } else if (rejectedFiles.length) {
        setQueuedAction(undefined);
        setNewImageFile(undefined);
        setNewImageUrl(undefined);

        setRejectedFile(rejectedFiles[0]);
      }
    },
  });

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.header}>
          <div
            className={styles.backButton}
            onClick={() => onClose({ keepPageSwitcherOpen: true })}
          >
            <ArrowStatefulIcon
              color="#3952FA"
              size={21}
              direction="left"
              className="c-box-heading__back-link-arrow"
            />
            Back to Accounts List
          </div>
          <CloseCircleIcon
            onClick={() => onClose()}
            className={styles.closeIcon}
          />
        </div>
        <div className={styles.body}>
          <Heading size={2} className={styles.heading}>
            Update your Account Information
          </Heading>
          <Heading size={4} addMarginAbove>
            Name
          </Heading>
          <TextInput
            value={pageName || ''}
            onChange={onPageNameChange}
            className={styles.pageNameInput}
          />
          <Heading size={4} addMarginAbove>
            Logo
          </Heading>

          {customSquarePicture === undefined ? (
            <i className="fas fa-spinner fa-spin" />
          ) : (
            <>
              {rejectedFile && (
                <Alert
                  type="error"
                  css={{ container: styles.alert, text: styles.alertText }}
                  closeIconProps={{
                    onClick: () => setRejectedFile(undefined),
                  }}
                >
                  {rejectedFile.name}
                  {rejectedFile.size >= UPLOAD_SIZE_LIMIT
                    ? ' (File Size Exceeds 8MB)'
                    : ' (Unaccepted File Type)'}
                </Alert>
              )}

              <div className={styles.customSquarePictureContainer}>
                {(customSquarePicture || newImageFile) && (
                  <ProfileImage
                    altText={`Business Logo for ${facebookPage.name}`}
                    profilePictureUrl={newImageUrl || customSquarePicture}
                    className={styles.avatar}
                    size={64}
                  />
                )}
                <div {...getRootProps()} style={{ width: '100%' }}>
                  <input type="file" {...getInputProps()} />
                  {customSquarePicture || newImageFile ? (
                    <PrimaryButton color="transparent-no-shadow">
                      Upload another logo image
                    </PrimaryButton>
                  ) : (
                    <div className={styles.uploadDropArea}>
                      <Upload />
                      <div>
                        {isDragActive ? (
                          'Drop logo image file here'
                        ) : (
                          <>
                            <span
                              className={styles.uploadDropAreaChooseFileLink}
                            >
                              Click&nbsp;
                            </span>
                            or drag your logo to this area to upload
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          <div className={styles.buttonsWrapper}>
            <PrimaryButton
              className={styles.cancelBtn}
              color="transparent-no-shadow"
              onClick={() => onClose({ keepPageSwitcherOpen: true })}
            >
              Cancel
            </PrimaryButton>
            <PrimaryButton
              onClick={onSave}
              disabled={busy || !queuedAction || pageName === ''}
              className={styles.saveBtn}
            >
              {busy && (
                <i
                  className={`fas fa-spinner fa-spin ${styles.saveBusySpinner}`}
                />
              )}
              Save Changes
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};
